import { useCallback, useMemo, useState } from "react";

import { addJWTInConfig } from "../../components/apiHooks/helpers";
import useApiRequestPost from "../../components/apiHooks/useApiRequestPost";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { parseTerminalError } from "../terminalError";
import { ITerminalActionResponse, TerminalEndpoint } from "./types";

const TERMINAL_HOST: string = process.env.TERMINAL_HOST ?? "";

export function extractTerminalErrorMessage(err: unknown): string {
	const e = err as {
		response?: {
			status?: number;
			data?: { error?: string; errors?: Array<{ detail: string; code: string }>; message?: string };
			statusText?: string;
		};
		message?: string;
	};

	const parsed = e?.response?.data ? parseTerminalError(e.response.data) : null;
	const beMessage = parsed?.detail ?? e?.response?.data?.message ?? e?.response?.statusText;

	if (beMessage) {
		const status = e?.response?.status;

		return status ? `(${status}) ${beMessage}` : beMessage;
	}

	return e?.message ?? "Terminal action failed";
}

const useTerminalAction = (endpoint: TerminalEndpoint) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const {
		state: { user },
	} = useConfigContext((state) => state);

	const url = `${TERMINAL_HOST}${endpoint}`;

	const payload = useMemo(
		() => ({
			url,
			triggerOnMount: false,
			axiosConfig: addJWTInConfig({ timeout: 60000 }, user?.session_id?.toString() ?? ""),
			body: undefined,
			swrConfig: {
				revalidateOnFocus: false,
				revalidateOnReconnect: false,
				revalidateOnMount: false,
				revalidateIfStale: false,
				dedupingInterval: 0,
				refreshInterval: 0,
				shouldRetryOnError: false,
			},
		}),
		[url, user?.session_id]
	);

	const { postData } = useApiRequestPost(payload);

	const submit = useCallback(async (): Promise<ITerminalActionResponse> => {
		setIsLoading(true);
		setError(null);

		try {
			const result = await postData();
			const data = result?.data as ITerminalActionResponse | undefined;

			if (!data) {
				throw new Error("Empty response from terminal endpoint");
			}

			if (data.status !== "success") {
				const message =
					parseTerminalError(data)?.detail ?? `Terminal returned status: ${data.status}`;

				throw new Error(message);
			}

			setIsLoading(false);

			return data;
		} catch (err) {
			const message = extractTerminalErrorMessage(err);

			console.error(`🔍 Terminal action ${endpoint} failed:`, err);
			setError(message);
			setIsLoading(false);
			throw new Error(message);
		}
	}, [postData, endpoint]);

	return { submit, isLoading, error };
};

export default useTerminalAction;
